import { useState, useEffect, useCallback } from "react";
import { supabase, requireOrganizationId } from "../lib/supabase";
import { logError } from "../lib/logError";

// ─── Types ───────────────────────────────────────────────────
export interface SubscriptionTier {
  id: string;
  name: string;
  display_name: string;
  price_monthly: number;
  price_yearly: number | null;
  max_projects: number;      // -1 = unlimited
  max_members: number;       // -1 = unlimited
  ai_credits_monthly: number;
  features: string[];
  sort_order: number;
}

export interface Subscription {
  id: string;
  organization_id: string;
  tier_id: string;
  status: "active" | "past_due" | "canceled" | "trialing" | "paused";
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  trial_ends_at: string | null;
  tier: SubscriptionTier | null;
}

export interface AiCreditsUsage {
  total_credits_used: number;
  total_requests: number;
  credits_by_feature: Record<string, number>;
  unique_users: number;
}

export type AiFeature =
  | "chat"
  | "report_generation"
  | "variation_analysis"
  | "pnl_insights"
  | "dashboard_insights"
  | "claims_insight"
  | "payment_analysis"
  | "receipt_analysis"
  | "expense_receipt"
  | "progress_claim"
  | "newsletter"
  | "description_gen"
  | "other";

/** Token usage metadata returned by Gemini edge functions */
export interface AiUsageMetadata {
  promptTokens?: number;
  completionTokens?: number;
  totalTokens?: number;
  model?: string;
}

/** Credit calculation: 1 credit = 1,000 tokens (configurable) */
const TOKENS_PER_CREDIT = 1000;

/** Calculate credits from token count */
export function tokensToCredits(totalTokens: number): number {
  if (!totalTokens || totalTokens <= 0) return 1; // minimum 1 credit per call
  return Math.ceil(totalTokens / TOKENS_PER_CREDIT);
}

// ─── Hook ────────────────────────────────────────────────────
export function useSubscription() {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [tiers, setTiers] = useState<SubscriptionTier[]>([]);
  const [creditsUsage, setCreditsUsage] = useState<AiCreditsUsage | null>(null);
  const [isInternalOrg, setIsInternalOrg] = useState(false);
  const [loading, setLoading] = useState(true);

  const orgId = requireOrganizationId();

  // Fetch subscription + tier + available tiers + org internal status
  const fetchSubscription = useCallback(async () => {
    try {
      const [subResult, tiersResult, orgResult] = await Promise.all([
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (supabase.from as any)("subscriptions")
          .select("*, tier:subscription_tiers(*)")
          .eq("organization_id", orgId)
          .maybeSingle(),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (supabase.from as any)("subscription_tiers")
          .select("*")
          .eq("is_active", true)
          .order("sort_order"),
        // Check if this is an internal/founding org (unlimited usage)
        supabase.from("organizations")
          .select("is_internal")
          .eq("id", orgId)
          .maybeSingle(),
      ]);

      if (subResult.error) {
        logError("[Subscription] Error fetching:", subResult.error);
      } else {
        setSubscription(subResult.data as Subscription | null);
      }

      if (tiersResult.error) {
        logError("[Subscription] Error fetching tiers:", tiersResult.error);
      } else {
        setTiers((tiersResult.data as SubscriptionTier[]) || []);
      }

      if (orgResult.error) {
        logError("[Subscription] Error fetching org:", orgResult.error);
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setIsInternalOrg(!!(orgResult.data as any)?.is_internal);
      }
    } catch (err) {
      logError("[Subscription] Fetch error:", err);
    }
  }, [orgId]);

  // Fetch current month's AI credits usage
  const fetchCreditsUsage = useCallback(async () => {
    try {
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.from as any)("ai_credits_usage")
        .select("feature, credits_used")
        .eq("organization_id", orgId)
        .gte("created_at", startOfMonth.toISOString());

      if (error) {
        logError("[Subscription] Credits usage error:", error);
        return;
      }

      const usage: AiCreditsUsage = {
        total_credits_used: 0,
        total_requests: 0,
        credits_by_feature: {},
        unique_users: 0,
      };

      if (data) {
        usage.total_requests = data.length;
        for (const row of data) {
          usage.total_credits_used += row.credits_used;
          usage.credits_by_feature[row.feature] =
            (usage.credits_by_feature[row.feature] || 0) + row.credits_used;
        }
      }

      setCreditsUsage(usage);
    } catch (err) {
      logError("[Subscription] Credits fetch error:", err);
    }
  }, [orgId]);

  // Load on mount
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchSubscription(), fetchCreditsUsage()]);
      setLoading(false);
    };
    load();
  }, [fetchSubscription, fetchCreditsUsage]);

  // ── Computed values ──
  const tier = subscription?.tier ?? null;
  const isActive = subscription?.status === "active" || subscription?.status === "trialing";
  const isPastDue = subscription?.status === "past_due";
  const isSuspended = !isInternalOrg && !!subscription && (
    subscription.status === "past_due" ||
    subscription.status === "canceled" ||
    subscription.status === "paused"
  );
  const creditsUsed = creditsUsage?.total_credits_used ?? 0;
  const creditsLimit = tier?.ai_credits_monthly ?? 0;

  // Internal orgs have unlimited credits
  const isUnlimited = isInternalOrg || creditsLimit === -1;
  const creditsRemaining = isUnlimited ? Infinity : Math.max(0, creditsLimit - creditsUsed);
  const creditsPercent = isUnlimited ? 0 : (creditsLimit > 0 ? Math.min(100, (creditsUsed / creditsLimit) * 100) : 0);
  const isOverLimit = !isUnlimited && creditsUsed >= creditsLimit && creditsLimit > 0;

  /* eslint-disable react-hooks/purity */
  const daysUntilRenewal = subscription
    ? Math.max(0, Math.ceil(
        (new Date(subscription.current_period_end).getTime() - Date.now()) /
          (1000 * 60 * 60 * 24)
      ))
    : 0;
  /* eslint-enable react-hooks/purity */

  // Check feature access
  const hasFeature = useCallback(
    (feature: string): boolean => {
      if (isInternalOrg) return true; // Internal orgs have all features
      if (!tier) return false;
      return tier.features.includes(feature);
    },
    [tier, isInternalOrg]
  );

  // Pre-flight AI credit check: call before invoking an AI edge function
  const canUseAi = useCallback(
    (feature: AiFeature): { allowed: boolean; reason?: string } => {
      // Internal orgs always allowed
      if (isInternalOrg) return { allowed: true };

      // No subscription = no AI
      if (!subscription || !isActive) {
        return { allowed: false, reason: "No active subscription" };
      }

      // Check if feature is in tier's feature list
      if (tier && !tier.features.includes("ai_" + feature) && !tier.features.includes("ai_all")) {
        return { allowed: false, reason: `Feature "${feature}" not included in your plan` };
      }

      // Check credit limit
      if (isOverLimit) {
        // Audit log, fire-and-forget (non-blocking)
        supabase.auth.getUser().then(({ data: { user } }) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (supabase.from as any)("audit_log").insert({
            organization_id: orgId,
            user_id: user?.id ?? null,
            action: "ai.credit_limit_reached",
            entity_type: "ai",
            entity_id: feature,
            metadata: { credits_used: creditsUsed, credits_limit: creditsLimit },
          }).then(() => {/* noop */}).catch(() => {/* silent */});
        });
        return { allowed: false, reason: "Monthly AI credit limit reached" };
      }

      return { allowed: true };
    },
    [isInternalOrg, subscription, isActive, tier, isOverLimit, orgId, creditsUsed, creditsLimit]
  );

  // Check project limit
  const canCreateProject = useCallback(
    (currentCount: number): boolean => {
      if (isInternalOrg) return true;
      if (!tier) return false;
      if (tier.max_projects === -1) return true; // unlimited
      return currentCount < tier.max_projects;
    },
    [tier, isInternalOrg]
  );

  // Check member limit
  const canAddMember = useCallback(
    (currentCount: number): boolean => {
      if (isInternalOrg) return true;
      if (!tier) return false;
      if (tier.max_members === -1) return true; // unlimited
      return currentCount < tier.max_members;
    },
    [tier, isInternalOrg]
  );

  // Check claim creation: currently unlimited for all tiers
  // but suspended orgs are blocked
  const canCreateClaim = useCallback(
    (): { allowed: boolean; reason?: string } => {
      if (isInternalOrg) return { allowed: true };
      if (isSuspended) {
        return { allowed: false, reason: "Your subscription is suspended. Please contact your administrator." };
      }
      if (!subscription || !isActive) {
        return { allowed: false, reason: "No active subscription" };
      }
      return { allowed: true };
    },
    [isInternalOrg, isSuspended, subscription, isActive]
  );

  /**
   * Log AI credit usage with accurate token-based metering.
   * 
   * @param feature - The AI feature category
   * @param usage - Token usage metadata from the edge function response
   * @param fallbackCredits - Fallback credit count if no token data available
   */
  const logAiUsage = useCallback(
    async (
      feature: AiFeature,
      usage?: AiUsageMetadata,
      fallbackCredits: number = 1
    ) => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        // Calculate credits from tokens, or use fallback
        const credits = usage?.totalTokens
          ? tokensToCredits(usage.totalTokens)
          : fallbackCredits;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error } = await (supabase.from as any)("ai_credits_usage").insert({
          organization_id: orgId,
          user_id: user.id,
          feature,
          credits_used: credits,
          model: usage?.model || null,
          metadata: {
            prompt_tokens: usage?.promptTokens || null,
            completion_tokens: usage?.completionTokens || null,
            total_tokens: usage?.totalTokens || null,
            is_internal: isInternalOrg,
          },
        });

        if (error) {
          logError("[AI Credits] Failed to log usage:", error);
        }
      } catch (err) {
        logError("[AI Credits] Log error:", err);
      }
    },
    [orgId, isInternalOrg]
  );

  return {
    // Data
    subscription,
    tier,
    tiers,
    creditsUsage,

    // State
    loading,
    isInternalOrg,

    // Computed
    isActive,
    isPastDue,
    isSuspended,
    isUnlimited,
    creditsUsed,
    creditsLimit,
    creditsRemaining,
    creditsPercent,
    isOverLimit,
    daysUntilRenewal,

    // Methods
    hasFeature,
    canUseAi,
    canCreateProject,
    canAddMember,
    canCreateClaim,
    logAiUsage,
    refresh: fetchSubscription,
    refreshCredits: fetchCreditsUsage,
  };
}
